/**
 * Miller Harris — Travel Bundle Gift (Canvas Pochette)
 *
 * Auto-adds a free Canvas Pochette to the cart when a valid Travel Bundle
 * is present (3+ eligible 10ml fragrances), and removes it when the cart
 * drops below the bundle threshold.
 *
 * The pouch is a £0 product, so there's nothing to discount — this script
 * only manages its presence in the cart. The £70 bundle pricing itself is
 * handled separately by the "Travel Bundle" order-discount Function.
 *
 * Deliberately self-contained and independent of mh-gwp-manager.js and
 * free-samples.js:
 *   - It does NOT hook window.fetch (those two already do; a third hook
 *     risks cascading cart re-fetches). Instead it listens to the cart
 *     events the theme already dispatches, plus a light poll on /cart.
 *   - Add/remove are idempotent and debounced, so reacting to other
 *     scripts' cart:updated events converges rather than looping.
 *   - The pouch line is tagged with `_bundle_gift: "true"`, NOT the GWP
 *     system's `_gwp_rule`. mh-gwp-manager.js removes any `_gwp_rule` line
 *     not in the GWP active-rules, which would delete the pouch; a separate
 *     property keeps it outside the GWP manager entirely.
 *
 * Config comes from the shop metafield mh-travel-bundle.config, rendered
 * by the travel-bundle-config snippet. Shape:
 *   {
 *     "bundleSize": 3,
 *     "pouchVariantId": 57254269878645,
 *     "eligibleVariantIds": [57254333677941, ...],
 *     "message": "Any 3 for £70"
 *   }
 */

(function () {
  'use strict';

  var CONFIG_ELEMENT_ID = 'travel-bundle-config';
  // The pouch is tagged with its OWN line-item property, deliberately NOT the
  // GWP system's `_gwp_rule`. mh-gwp-manager.js runs an orphan cleanup that
  // removes any `_gwp_rule` line whose rule isn't in the GWP active-rules
  // metafield — it would delete our pouch on sight. Using a separate property
  // keeps the pouch fully outside the GWP manager's control.
  var GIFT_PROPERTY = '_bundle_gift';
  var GIFT_PROPERTY_VALUE = 'true';

  var config = null;
  var processing = false;
  var checkTimer = null;

  function init() {
    var el = document.getElementById(CONFIG_ELEMENT_ID);
    if (!el) return;
    try {
      config = JSON.parse(el.textContent);
    } catch (e) {
      config = null;
    }
    if (!config || !config.pouchVariantId || !Array.isArray(config.eligibleVariantIds) || !config.eligibleVariantIds.length) {
      return; // nothing configured — stay dormant
    }
    if (!config.bundleSize) config.bundleSize = 3;

    // Normalise eligible IDs to numbers for cart-line comparison.
    config.eligibleVariantIds = config.eligibleVariantIds.map(function (id) { return Number(id); });
    config.pouchVariantId = Number(config.pouchVariantId);

    debouncedCheck();
    observeCart();
  }

  // ── Cart helpers ──────────────────────────────────────────────────────

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  function addPouch(qty) {
    var props = {};
    props[GIFT_PROPERTY] = GIFT_PROPERTY_VALUE;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: config.pouchVariantId, quantity: qty, properties: props }] })
    });
  }

  // Set the pouch line to an exact quantity (0 removes it). Used to keep the
  // number of pouches equal to the number of complete bundles in the cart.
  function setPouchQty(key, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
  }

  // ── Eligibility ───────────────────────────────────────────────────────

  // Count eligible 10ml *units* in the cart, ignoring the pouch itself and
  // any other gift/sample lines. Quantity-aware: 1 line of qty 3 counts as 3.
  function eligibleUnitCount(cart) {
    var count = 0;
    cart.items.forEach(function (item) {
      var props = item.properties || {};
      if (props[GIFT_PROPERTY]) return;        // skip our pouch
      if (props._gwp_rule) return;             // skip GWP gifts
      if (props._free_sample) return;          // skip free samples
      if (config.eligibleVariantIds.indexOf(item.variant_id) !== -1) {
        count += item.quantity || 0;
      }
    });
    return count;
  }

  function findPouchLine(cart) {
    return cart.items.find(function (item) {
      return item.variant_id === config.pouchVariantId &&
        item.properties && item.properties[GIFT_PROPERTY] === GIFT_PROPERTY_VALUE;
    });
  }

  // ── Core ──────────────────────────────────────────────────────────────

  function check() {
    if (processing) return;
    processing = true;

    getCart().then(function (cart) {
      if (!cart || !Array.isArray(cart.items)) return;

      // One pouch per COMPLETE bundle: 3 eligible -> 1, 6 -> 2, 9 -> 3, etc.
      var desired = Math.floor(eligibleUnitCount(cart) / config.bundleSize);
      var pouch = findPouchLine(cart);
      var currentQty = pouch ? (pouch.quantity || 0) : 0;

      if (desired === currentQty) {
        return; // already correct — converged
      }
      if (pouch) {
        // Adjust the existing pouch line up/down (qty 0 removes it).
        return setPouchQty(pouch.key, desired).then(notifyCartUpdated);
      }
      if (desired > 0) {
        return addPouch(desired).then(notifyCartUpdated);
      }
    }).catch(function () { /* silent */ }).then(function () {
      processing = false;
    });
  }

  function debouncedCheck() {
    clearTimeout(checkTimer);
    checkTimer = setTimeout(check, 400);
  }

  function notifyCartUpdated() {
    // Let the theme (and the other cart scripts) re-render. Our own add/
    // remove is idempotent, so the re-entrant check this may trigger just
    // confirms the converged state and stops.
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('cart:quantity-updated', { detail: document }));
    var drawer = document.querySelector('[data-cart-drawer]');
    if (drawer) document.dispatchEvent(new CustomEvent('cart-drawer:updated'));
  }

  // ── Observe cart changes ──────────────────────────────────────────────

  function observeCart() {
    var events = ['cart:updated', 'cart:quantity-updated', 'cart:item:added', 'cart:item:removed', 'cart:rerendered', 'cart-drawer:updated'];
    events.forEach(function (name) {
      document.addEventListener(name, debouncedCheck);
    });
    document.addEventListener('shopify:section:load', debouncedCheck);

    // Cart page uses full-page navigation for some actions; poll lightly.
    if (window.location.pathname === '/cart') {
      setInterval(debouncedCheck, 2500);
    }
  }

  // ── Boot ──────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
